import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase, Speaker } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { speakers as defaultSpeakers } from '@/data/speakers';
import { Button } from "@/components/ui/button";
import SpeakerCard from './speakers/SpeakerCard';

const SpeakersSection = ({ hideNickSmirnov = false }: { hideNickSmirnov?: boolean } = {}) => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const { toast } = useToast();

  useEffect(() => {
    const fetchSpeakers = async () => {
      try {
        const { data, error } = await supabase
          .from('speakers')
          .select('*')
          .order('name')
          .limit(20);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          const mappedSpeakers: Speaker[] = data.map(speaker => ({
            ...speaker,
            id: speaker.id,
            name: speaker.name,
            title: speaker.title,
            slug: speaker.slug || speaker.name.toLowerCase().replace(/\s+/g, '-'),
            bio: '',
            image_url: speaker.image_url,
            topic: speaker.topic || null,
            twitter_url: speaker.twitter_url || null,
            linkedin_url: speaker.linkedin_url || null,
            github_url: speaker.github_url || null,
            company_name: speaker.company_name,
            company_logo: speaker.company_logo,
            conference_id: speaker.conference_id,
            created_at: speaker.created_at,
            updated_at: speaker.updated_at
          }));
          
          // Merge with local speakers that aren't in the database yet
          const dbSpeakerNames = mappedSpeakers.map(s => s.name.toLowerCase());
          const localOnlySpeakers = defaultSpeakers
            .filter(s => !dbSpeakerNames.includes(s.name.toLowerCase()))
            .map(s => ({
              id: s.id || `local-${s.name}`,
              name: s.name,
              title: s.title || '',
              bio: s.bio || '',
              slug: s.slug,
              image_url: s.image_url,
              topic: s.topic,
              company_name: s.company_name,
              company_logo: s.company_logo,
              twitter_url: s.twitter_url,
              linkedin_url: s.linkedin_url,
              github_url: s.github_url,
              conference_id: s.conference_id
            }));
          
          setSpeakers([...mappedSpeakers, ...localOnlySpeakers]);
        }
      } catch (error) {
        console.error('Error fetching speakers:', error);
        toast({
          title: "Notice",
          description: "Using default speaker data",
          variant: "default",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSpeakers();
  }, [toast]);

  // Filter out Soren, Kanishka, Jim Morrish, Liran Zvibel, Puneet Jindal, Ian Glazebrook, Becky Davis, and Benjamin from display
  const finalSpeakers: Speaker[] = speakers.length > 0 
    ? speakers.filter(s => 
        s.name !== 'Soren F Mortensen' && 
        s.name !== 'Kanishka Narayan' &&
        !s.name.toLowerCase().includes('akis papageorgiou') &&
        !s.name.toLowerCase().includes('jim morrish') &&
        !s.name.toLowerCase().includes('liran zvibel') &&
        !s.name.toLowerCase().includes('puneet jindal') &&
        !s.name.toLowerCase().includes('ian glazebrook') &&
        !s.name.toLowerCase().includes('becky davis') &&
        !s.name.toLowerCase().includes('benjamin summerfield') &&
          !s.name.toLowerCase().includes('charles') &&
          !s.name.toLowerCase().includes('chuck') &&
          !s.name.toLowerCase().includes('pip white') &&
          !s.name.toLowerCase().includes('jay cruiz') &&
          !s.name.toLowerCase().includes('nick smirnov') &&
          !s.name.toLowerCase().includes('peter muld') &&
          !s.name.toLowerCase().includes('mo hamdy') &&
          !s.name.toLowerCase().includes('scott rowlandson') &&
          !s.name.toLowerCase().includes('philip gaffney')
        )
    : defaultSpeakers
        .filter(s => 
          s.name !== 'Soren F Mortensen' && 
          s.name !== 'Kanishka Narayan' &&
          !s.name.toLowerCase().includes('akis papageorgiou') &&
          !s.name.toLowerCase().includes('jim morrish') &&
          !s.name.toLowerCase().includes('liran zvibel') &&
          !s.name.toLowerCase().includes('puneet jindal') &&
          !s.name.toLowerCase().includes('ian glazebrook') &&
          !s.name.toLowerCase().includes('becky davis') &&
          !s.name.toLowerCase().includes('benjamin summerfield') &&
          !s.name.toLowerCase().includes('charles') &&
          !s.name.toLowerCase().includes('chuck') &&
          !s.name.toLowerCase().includes('pip white') &&
          !s.name.toLowerCase().includes('jay cruiz') &&
          !s.name.toLowerCase().includes('nick smirnov') &&
          !s.name.toLowerCase().includes('peter muld') &&
          !s.name.toLowerCase().includes('mo hamdy') &&
          !s.name.toLowerCase().includes('scott rowlandson') &&
          !s.name.toLowerCase().includes('philip gaffney')
        )
        .map(s => ({
          id: s.id || `default-${s.name}`,
          name: s.name,
          title: s.title || '',
          bio: s.bio || '',
          slug: s.slug,
          image_url: s.image_url,
          topic: s.topic,
          company_name: s.company_name,
          company_logo: s.company_logo,
          twitter_url: s.twitter_url,
          linkedin_url: s.linkedin_url,
          github_url: s.github_url,
          conference_id: s.conference_id
        }));

  // Custom ordering: David Groves #1, Akis #2, Nayur #3, Tejas #4, Ramyani #5, Marcus #6, Ankit Jain #7, then the existing order
  const displaySpeakers = (() => {
    const david = finalSpeakers.find(s => s.name.toLowerCase().includes('david groves'));
    const akis = finalSpeakers.find(s => s.name.toLowerCase().includes('akis'));
    const marcus = finalSpeakers.find(s => s.name.toLowerCase().includes('marcus vinicius martinez'));
    const tejas = finalSpeakers.find(s => s.name.toLowerCase().includes('tejas joshi'));
    const peter = finalSpeakers.find(s => s.name.toLowerCase().includes('peter muld'));
    const pipWhite = finalSpeakers.find(s => s.name.toLowerCase().includes('pip white'));
    const philip = finalSpeakers.find(s => s.name.toLowerCase().includes('philip gaffney'));
    const nayur = finalSpeakers.find(s => s.name.toLowerCase().includes('nayur khan'));
    const ramyani = finalSpeakers.find(s => s.name.toLowerCase().includes('ramyani basu'));
    const charles = finalSpeakers.find(s => s.name.toLowerCase().includes('charles') || s.name.toLowerCase().includes('chuck'));
    const ankit = finalSpeakers.find(s => s.name.toLowerCase().includes('ankit jain'));
    const yael = finalSpeakers.find(s => s.name.toLowerCase().includes('yael misrahi'));
    const organizers = finalSpeakers.filter(s => 
      s.name.toLowerCase().includes('chris eastham') ||
      s.name.toLowerCase().includes('ian glazebrook') ||
      s.name.toLowerCase().includes('jay cruiz') ||
      s.name.toLowerCase().includes('javier campos') ||
      s.name.toLowerCase().includes('jeremy sosabowski') ||
      s.name.toLowerCase().includes('mark sage')
    ).sort((a, b) => {
      const order = ['chris eastham', 'ian glazebrook', 'jay cruiz', 'javier campos', 'jeremy sosabowski', 'mark sage'];
      const aIdx = order.findIndex(name => a.name.toLowerCase().includes(name));
      const bIdx = order.findIndex(name => b.name.toLowerCase().includes(name));
      return aIdx - bIdx;
    });
    
    const regularSpeakers = finalSpeakers.filter(s => 
      s !== david &&
      s !== akis &&
      s !== marcus &&
      s !== tejas &&
      s !== peter &&
      s !== pipWhite &&
      s !== philip &&
      s !== nayur &&
      s !== ramyani && 
      s !== yael &&
      s !== charles &&
      !organizers.includes(s)
    );
    
    const result = [];
    if (david) result.push(david);
    if (akis) result.push(akis);
    if (nayur) result.push(nayur);
    if (tejas) result.push(tejas);
    if (ramyani) result.push(ramyani);
    if (marcus) result.push(marcus);
    if (peter) result.push(peter);
    if (pipWhite) result.push(pipWhite);
    if (philip) result.push(philip);
    result.push(...regularSpeakers);
    result.push(...organizers);
    // Mo Hamdy and Scott Rowlandson hidden from listings (profiles remain active)
    // Yael Misrahi hidden from homepage speaker section (profile remains active)
    if (charles) result.push(charles);

    // Ankit Jain pinned to 7th place in the listing (index 6)
    if (ankit) {
      const current = result.indexOf(ankit);
      if (current !== -1) result.splice(current, 1);
      result.splice(6, 0, ankit);
    }

    return result;
  })();




  console.log('Final speakers count:', displaySpeakers.length);
  console.log('Speaker names:', displaySpeakers.map(s => s.name));

  return (
    <section id="speakers" className="py-20 bg-gradient-to-b from-black via-blue-700 to-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <Link to="/tickets">
            <Button 
              className="mb-12 text-lg px-8 py-6 bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white font-semibold shadow-lg"
            >
              Register Now
            </Button>
          </Link>

          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Keynotes & Speakers 2026
            </h2>
            <p className="max-w-3xl mx-auto text-lg text-blue-200">
              Explore the exciting lineup of sessions and events
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 gap-3 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-6">
              {[...Array(8)].map((_, index) => (
                <Card 
                  key={index} 
                  className="bg-white/20 border-blue-500/20 backdrop-blur-sm overflow-hidden"
                >
                  <div className={`relative ${isMobile ? 'h-32' : 'h-56'} bg-blue-900/30`}>
                    <Skeleton className="h-full w-full" />
                  </div>
                  <CardContent className={`${isMobile ? 'p-3' : 'p-6'} relative text-left`}>
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-4" />
                    <Skeleton className="h-12 w-full mb-2 rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-6">
              {displaySpeakers.map((speaker, index) => (
                <SpeakerCard 
                  key={speaker.id} 
                  speaker={speaker}
                  isMobile={isMobile}
                  index={index}
                />
              ))}
            </div>
          )}
          
          {/* Register Now button below the speakers section - visible on all devices */}
          <div className="mt-16">
            <Link to="/tickets">
              <Button 
                className={`${isMobile ? 'w-full' : 'px-10 py-6'} text-lg bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white font-semibold shadow-lg`}
              >
                Register Now
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpeakersSection;
